/**
 * API Request Interceptors
 * 
 * Provides centralized request/response handling with:
 * - Automatic retries with exponential backoff + jitter
 * - Request/response logging
 * - Error transformation
 * - Timeout handling
 * - Cold start detection
 */

/**
 * Calculate exponential backoff delay with jitter
 * 
 * Uses full jitter strategy for better distributed retries
 * Formula: random(0, min(maxDelay, baseDelay * 2^attempt))
 * 
 * @param attempt - Current retry attempt (0-based)
 * @param baseDelay - Base delay in ms (default: 1000)
 * @param maxDelay - Maximum delay in ms (default: 30000)
 * @returns Delay in milliseconds
 * 
 * @example
 * ```ts
 * const delay = calculateBackoffWithJitter(0); // 0-1000ms
 * const delay = calculateBackoffWithJitter(1); // 0-2000ms
 * const delay = calculateBackoffWithJitter(3); // 0-8000ms
 * ```
 */
export function calculateBackoffWithJitter(
  attempt: number,
  baseDelay: number = 1000,
  maxDelay: number = 30000
): number {
  // Exponential backoff: baseDelay * 2^attempt
  const exponentialDelay = baseDelay * Math.pow(2, attempt);
  
  // Cap at maxDelay
  const cappedDelay = Math.min(exponentialDelay, maxDelay);
  
  // Full jitter: random between 0 and cappedDelay
  return Math.floor(Math.random() * cappedDelay);
}

/**
 * Retry configuration
 */
export interface RetryConfig {
  /**
   * Maximum number of retry attempts
   * @default 3
   */
  maxAttempts?: number;
  
  /**
   * Base delay between retries (ms)
   * @default 1000
   */
  baseDelay?: number;
  
  /**
   * Maximum delay between retries (ms)
   * @default 30000
   */
  maxDelay?: number;
  
  /**
   * HTTP status codes that should trigger retry
   * @default [408, 429, 500, 502, 503, 504]
   */
  retryableStatusCodes?: number[];
  
  /**
   * Should retry on network errors
   * @default true
   */
  retryOnNetworkError?: boolean;
  
  /**
   * Custom retry condition
   */
  shouldRetry?: (error: any, attempt: number) => boolean;
  
  /**
   * Callback when retry occurs
   */
  onRetry?: (error: any, attempt: number, delay: number) => void;
}

const DEFAULT_RETRY_CONFIG: Required<Omit<RetryConfig, 'shouldRetry' | 'onRetry'>> = {
  maxAttempts: 3,
  baseDelay: 1000,
  maxDelay: 30000,
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
  retryOnNetworkError: true,
};

/**
 * Check if error is retryable
 */
function isRetryableError(error: any, config: RetryConfig): boolean {
  const mergedConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
  
  // Custom retry logic
  if (config.shouldRetry) {
    return config.shouldRetry(error, 0);
  }
  
  // Network errors
  if (mergedConfig.retryOnNetworkError) {
    if (
      error.message?.includes('fetch') ||
      error.message?.includes('network') ||
      error.name === 'NetworkError' ||
      error.name === 'TypeError'
    ) {
      return true;
    }
  }
  
  // HTTP status codes
  if (error.status && mergedConfig.retryableStatusCodes.includes(error.status)) {
    return true;
  }
  
  // Supabase-specific errors
  if (error.code === 'PGRST301') {
    // Cold start - definitely retry
    return true;
  }
  
  return false;
}

/**
 * Sleep for specified duration
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Fetch with automatic retry and exponential backoff
 * 
 * @example
 * ```ts
 * const response = await fetchWithRetry('https://api.example.com/data', {
 *   method: 'GET',
 *   headers: { 'Authorization': 'Bearer token' }
 * }, {
 *   maxAttempts: 5,
 *   baseDelay: 500,
 *   onRetry: (error, attempt, delay) => {
 *     console.log(`Retry ${attempt} after ${delay}ms`);
 *   }
 * });
 * ```
 */
export async function fetchWithRetry(
  url: string,
  init?: RequestInit,
  config: RetryConfig = {}
): Promise<Response> {
  const mergedConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
  let lastError: any;
  
  for (let attempt = 0; attempt < mergedConfig.maxAttempts; attempt++) {
    try {
      const response = await fetch(url, init);
      
      // Success
      if (response.ok) {
        return response;
      }
      
      // Check if we should retry this status
      if (!mergedConfig.retryableStatusCodes.includes(response.status)) {
        return response; // Non-retryable error, return as-is
      }
      
      lastError = new Error(`HTTP ${response.status}: ${response.statusText}`);
      (lastError as any).status = response.status;
      (lastError as any).response = response;
      
    } catch (error) {
      lastError = error;
      
      // Check if error is retryable
      if (!isRetryableError(error, config)) {
        throw error;
      }
    }
    
    // Last attempt - don't retry
    if (attempt === mergedConfig.maxAttempts - 1) {
      break;
    }
    
    // Calculate backoff delay
    const delay = calculateBackoffWithJitter(
      attempt,
      mergedConfig.baseDelay,
      mergedConfig.maxDelay
    );
    
    // Call onRetry callback
    if (config.onRetry) {
      config.onRetry(lastError, attempt + 1, delay);
    }
    
    // Wait before retrying
    await sleep(delay);
  }
  
  // All attempts failed
  throw lastError;
}

/**
 * Request interceptor for Supabase client
 * 
 * Adds:
 * - Request ID for tracing
 * - Timeout handling
 * - Request logging (dev only)
 * 
 * @example
 * ```ts
 * import { createClient } from '@supabase/supabase-js';
 * import { supabaseRequestInterceptor } from './interceptors';
 * 
 * const supabase = createClient(url, key, {
 *   global: {
 *     fetch: supabaseRequestInterceptor(fetch.bind(globalThis)),
 *   },
 * });
 * ```
 */
export function supabaseRequestInterceptor(
  originalFetch: typeof fetch
): typeof fetch {
  return async function interceptedFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const method = init?.method || 'GET';
    const requestId = crypto.randomUUID().slice(0, 8);
    
    // Log request (dev only)
    if (import.meta.env.DEV) {
      console.log(`[API ${requestId}] ${method} ${url}`);
    }
    
    const startTime = Date.now();
    
    try {
      // Add timeout (30s default for Supabase cold starts)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      const response = await originalFetch(input, {
        ...init,
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      const duration = Date.now() - startTime;
      
      // Log response (dev only)
      if (import.meta.env.DEV) {
        console.log(
          `[API ${requestId}] ${response.status} ${response.statusText} (${duration}ms)`
        );
        
        // Warn on slow requests
        if (duration > 5000) {
          console.warn(
            `[API ${requestId}] Slow request detected: ${duration}ms (possible cold start)`
          );
        }
      }
      
      return response;
      
    } catch (error: any) {
      const duration = Date.now() - startTime;
      
      // Log error (dev only)
      if (import.meta.env.DEV) {
        console.error(
          `[API ${requestId}] Error after ${duration}ms:`,
          error.message
        );
      }
      
      throw error;
    }
  };
}

/**
 * Wrap async function with retry logic
 * 
 * @example
 * ```ts
 * const fetchUser = withRetry(
 *   async (userId: string) => {
 *     const { data, error } = await supabase
 *       .from('users')
 *       .select('*')
 *       .eq('id', userId)
 *       .single();
 *     
 *     if (error) throw error;
 *     return data;
 *   },
 *   { maxAttempts: 3, baseDelay: 500 }
 * );
 * 
 * const user = await fetchUser('123');
 * ```
 */
export function withRetry<TArgs extends any[], TReturn>(
  fn: (...args: TArgs) => Promise<TReturn>,
  config: RetryConfig = {}
): (...args: TArgs) => Promise<TReturn> {
  return async function (...args: TArgs): Promise<TReturn> {
    const mergedConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
    let lastError: any;
    
    for (let attempt = 0; attempt < mergedConfig.maxAttempts; attempt++) {
      try {
        return await fn(...args);
      } catch (error) {
        lastError = error;
        
        // Check if error is retryable
        if (!isRetryableError(error, config)) {
          throw error;
        }
        
        // Last attempt - don't retry
        if (attempt === mergedConfig.maxAttempts - 1) {
          break;
        }
        
        // Calculate backoff delay
        const delay = calculateBackoffWithJitter(
          attempt,
          mergedConfig.baseDelay,
          mergedConfig.maxDelay
        );
        
        // Call onRetry callback
        if (config.onRetry) {
          config.onRetry(lastError, attempt + 1, delay);
        }
        
        // Wait before retrying
        await sleep(delay);
      }
    }
    
    throw lastError;
  };
}

